import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';

import { SkillTaxonomySection } from '@eva/types/dto';
import { removePathsFromError, stringifyError } from 'shared/functions';
import type {
  SkillTaxonomyTreeDto,
  SkillTaxonomyTreeGroupDto,
  SkillTaxonomyTreeSkillDto,
  SkillTProficiencyDto,
} from '@eva/types/dto';
import { SkillDto } from '@eva/types/dto/skills';

import { updateNodeExpandedState, updateTreeExpandedState } from 'components/TreeMenu';
import { getSkillGroupId } from 'entities/skillTaxonomy/SkillTaxonomyTreeItemForm/functions';

import { SkillTaxnomyChildDto } from './types';

export const getSkillTaxonomyById = (
  skillTaxonomies: SkillTaxonomySection[],
  skillTaxonomyId: SkillTaxonomySection['skillTaxonomyId'],
) => find(skillTaxonomies, { skillTaxonomyId });

export const isSkill = (data: SkillTaxnomyChildDto): data is SkillTaxonomyTreeSkillDto => 'skillId' in data;

export const isSkillGroup = (data: SkillTaxnomyChildDto): data is SkillTaxonomyTreeGroupDto => 'skillGroupId' in data;

export const expandHighlightedSkills = (
  skillTaxonomy: SkillTaxnomyChildDto,
  defaultExpandedSkills: number[],
): SkillTaxnomyChildDto & { expanded?: boolean } => {
  if (!skillTaxonomy) return skillTaxonomy;

  if (isSkill(skillTaxonomy)) {
    return { ...skillTaxonomy, expanded: defaultExpandedSkills.includes(skillTaxonomy.skillId) };
  }

  if (!skillTaxonomy.children) {
    return skillTaxonomy;
  }

  const skillGroupChildren = skillTaxonomy.children.map((child) =>
    expandHighlightedSkills(child, defaultExpandedSkills),
  );

  const isExpanded = skillGroupChildren.some((child) => child.expanded);
  return { ...skillTaxonomy, children: skillGroupChildren, expanded: isExpanded };
};

export const handleExpandTree = ({
  tree,
  isExpand,
}: {
  tree: SkillTaxonomyTreeDto & {
    expanded?: boolean;
  };
  isExpand: boolean;
}) => {
  const treeCopy = cloneDeep(tree);
  return updateTreeExpandedState(treeCopy, isExpand, (root, node) => getSkillGroupId(root) === getSkillGroupId(node));
};

export const onExpandGroup =
  ({
    setSkillTaxonomyTree,
    skillTaxonomyTree,
  }: {
    setSkillTaxonomyTree: (tree: any) => void;
    skillTaxonomyTree: any;
  }) =>
  (group) => {
    const treeCopy = cloneDeep(skillTaxonomyTree);
    const treeWIthIsExpandedGroup = updateNodeExpandedState(treeCopy, group, getSkillGroupId, !group.expanded);
    setSkillTaxonomyTree(treeWIthIsExpandedGroup);
  };

export const mapFormDataToBody = (data: { skills: any[] }) =>
  data.skills.reduce(
    (result, current) => [
      ...result,
      {
        skillId: current.skillId,
        skillProficiencyValue: current.skillProficiencyValue || null,
      },
    ],
    [],
  );

export const getProficiencyOptions = (skillProficiencies: SkillTProficiencyDto[]) =>
  skillProficiencies.reduce(
    (result, current) => [
      ...result,
      {
        label: current.name,
        value: current.sortOrder,
      },
    ],
    [],
  );

export const convertError = (error: any) => {
  const errorWithoutPath = removePathsFromError(error);
  return stringifyError(errorWithoutPath);
};

export const convertSkillTaxonomyTreeToSkillDtos = (tree: SkillTaxonomyTreeDto): SkillDto[] => {
  const skills: SkillDto[] = [];

  function traverse(node: SkillTaxonomyTreeDto) {
    node.children.forEach((child) => {
      if ('skillId' in child) {
        skills.push(child);
      } else if ('children' in child) {
        // @ts-expect-error
        traverse(child);
      }
    });
  }

  traverse(tree);

  return skills;
};
